import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Layers, Paintbrush, UtensilsCrossed } from 'lucide-react';
import { PRODUCT_PILLARS } from '../data/productCatalog';
import { BROCHURE_PDF } from '../data/siteContent';

const pillarIcon = (slug) => {
  const map = {
    'epoxy-flooring': Layers,
    'polyurethane-flooring': Droplets,
    'wall-coatings': Paintbrush,
    'food-grade-systems': UtensilsCrossed,
  };
  return map[slug] || Layers;
};

const ProductsHub = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="products-hub-page min-h-screen bg-[#f4f6f9]">
      <section className="relative overflow-hidden pb-24 pt-16 md:pb-28 md:pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900 via-brand-800 to-[#06202d]" />
        <div className="absolute inset-0 industrial-grid opacity-[0.1]" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#f4f6f9] to-transparent" />
        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6">
          <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-200">
            Product catalogue
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-white md:text-5xl lg:text-[3.25rem] lg:leading-tight">
            SV Bond product range
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-brand-100/95 md:text-lg">
            Choose a category to view systems: product lines (Beck Bond / SV Bond) with description and applications from the brochure.
          </p>
        </div>
      </section>

      <section className="relative z-20 mx-auto -mt-16 max-w-6xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2">
          {PRODUCT_PILLARS.map((pillar) => {
            const Icon = pillarIcon(pillar.slug);
            return (
              <Link
                key={pillar.slug}
                to={`/products/${pillar.slug}`}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-[0_8px_40px_rgba(13,40,71,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-[0_16px_48px_rgba(13,40,71,0.12)]"
              >
                <div className="h-1.5 w-full bg-gradient-to-r from-brand-700 via-[#1a6b56] to-brand-500 opacity-90 transition-opacity group-hover:opacity-100" />
                <div className="flex flex-1 flex-col p-7 md:p-8">
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100/80 text-brand-800 ring-1 ring-brand-200/40 transition-transform group-hover:scale-105">
                      <Icon className="h-7 w-7" strokeWidth={1.75} />
                    </span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 text-neutral-400 transition-all group-hover:border-brand-300 group-hover:bg-brand-50 group-hover:text-brand-700">
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                  <h2 className="font-heading text-xl font-bold leading-snug text-neutral-900 md:text-2xl">{pillar.title}</h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-neutral-600">{pillar.overview.what}</p>
                  <div className="mt-6 flex items-center gap-2 border-t border-neutral-100 pt-5">
                    <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-600">
                      {pillar.systems.length} system{pillar.systems.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-xs font-medium text-brand-700 opacity-0 transition-opacity group-hover:opacity-100">
                      View details →
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <p className="mt-12 text-center text-sm text-neutral-500">
          Full technical data:{' '}
          <a
            href={BROCHURE_PDF}
            className="font-semibold text-brand-800 underline decoration-brand-300 underline-offset-2 transition hover:text-brand-600"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download brochure (PDF)
          </a>
        </p>
      </section>
    </div>
  );
};

export default ProductsHub;
